import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  DtoAdminGetApplicationResponse,
  DtoAdminGetPreapprovalEmailStatusResp,
  DtoAdminSearchBorrowerApplication,
} from "@porchpass/api-client";
import { applicationLosId } from "src/api/preApproval/mappers";
import {
  getAllConditionsForCounts,
  getEmailStatus,
  getNotesCount,
  generateLetter,
  getPreapprovalDocumentPdf,
  lookupApplication,
  syncToDigiFi,
} from "src/api/preApproval";
import { isOnLetter } from "src/api/preApproval/helpers";
import type {
  ConditionCounts,
  EmailRecipient,
  PreApprovalCondition,
} from "src/api/preApproval/types";

export type WorkspaceActionState = "idle" | "generating" | "syncing";
export type PdfState = "loading" | "ready" | "missing" | "error";
export type EmailStatusState = "loading" | "ready" | "error";
export type BootstrapState = "loading" | "ready" | "error";

interface WorkspaceContextValue {
  externalId: string;
  losApplicationId: string | null;
  application: DtoAdminGetApplicationResponse | null;
  bootstrapState: BootstrapState;
  bootstrapError: string | null;
  actionState: WorkspaceActionState;
  generationError: string | null;
  syncError: string | null;
  emailStatus: DtoAdminGetPreapprovalEmailStatusResp | null;
  emailStatusState: EmailStatusState;
  emailStatusError: string | null;
  pdfBlobUrl: string | null;
  pdfState: PdfState;
  pdfError: string | null;
  recipients: EmailRecipient[] | null;
  isSendModalOpen: boolean;
  isPreviewMaximized: boolean;
  conditionCounts: ConditionCounts;
  conditionsRefreshKey: number;
  setSendModalOpen: (open: boolean) => void;
  togglePreviewMaximized: () => void;
  refreshEmailStatus: () => Promise<void>;
  refreshCounts: () => Promise<void>;
  bumpConditionsRefresh: () => void;
  handleGenerate: () => Promise<void>;
  handleSync: () => Promise<void>;
  openSendModal: () => Promise<void>;
  refreshPdf: () => Promise<PdfState>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const EMPTY_COUNTS: ConditionCounts = {
  customer: 0,
  funding: 0,
  seller: 0,
  notes: 0,
};

function deriveConditionCounts(
  conditions: PreApprovalCondition[],
): Pick<ConditionCounts, "customer" | "funding" | "seller"> {
  return {
    customer: conditions.filter(
      (condition) =>
        condition.section === "customer" && isOnLetter(condition.status),
    ).length,
    funding: conditions.filter(
      (condition) =>
        condition.section === "funding" && isOnLetter(condition.status),
    ).length,
    seller: conditions.filter(
      (condition) =>
        condition.section === "seller" && isOnLetter(condition.status),
    ).length,
  };
}

export function WorkspaceProvider({
  externalId,
  borrower: initialBorrower,
  children,
}: {
  externalId: string;
  borrower: DtoAdminSearchBorrowerApplication | null;
  children: React.ReactNode;
}) {
  const [application, setApplication] =
    useState<DtoAdminGetApplicationResponse | null>(null);
  const [losApplicationId, setLosApplicationId] = useState<string | null>(
    initialBorrower ? applicationLosId(initialBorrower) : null,
  );
  const [bootstrapState, setBootstrapState] = useState<BootstrapState>(
    initialBorrower ? "ready" : "loading",
  );
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<WorkspaceActionState>("idle");
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] =
    useState<DtoAdminGetPreapprovalEmailStatusResp | null>(null);
  const [emailStatusState, setEmailStatusState] =
    useState<EmailStatusState>("loading");
  const [emailStatusError, setEmailStatusError] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfState, setPdfState] = useState<PdfState>("loading");
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<EmailRecipient[] | null>(null);
  const [isSendModalOpen, setSendModalOpen] = useState(false);
  const [isPreviewMaximized, setIsPreviewMaximized] = useState(false);
  const [conditionCounts, setConditionCounts] =
    useState<ConditionCounts>(EMPTY_COUNTS);
  const [conditionsRefreshKey, setConditionsRefreshKey] = useState(0);
  const pdfUrlRef = useRef<string | null>(null);
  const pdfRequestIdRef = useRef(0);
  const emailStatusRequestIdRef = useRef(0);
  const bootstrapRequestIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (initialBorrower) {
      setLosApplicationId(applicationLosId(initialBorrower));
      setBootstrapState("ready");
      setBootstrapError(null);
    } else {
      setBootstrapState("loading");
      setBootstrapError(null);
    }

    const requestId = ++bootstrapRequestIdRef.current;

    lookupApplication(externalId)
      .then((nextApplication) => {
        if (
          !mountedRef.current ||
          requestId !== bootstrapRequestIdRef.current
        ) {
          return;
        }
        setApplication(nextApplication);
        setLosApplicationId(nextApplication.los_application_id ?? null);
        setBootstrapState("ready");
        setBootstrapError(null);
      })
      .catch((error) => {
        if (
          !mountedRef.current ||
          requestId !== bootstrapRequestIdRef.current
        ) {
          return;
        }
        if (!initialBorrower) {
          setBootstrapState("error");
          setBootstrapError(
            error instanceof Error
              ? error.message
              : "Unable to load this application.",
          );
          return;
        }
        setApplication(null);
      });
  }, [externalId, initialBorrower]);

  const refreshCounts = useCallback(async () => {
    try {
      const [conditions, notesCount] = await Promise.all([
        getAllConditionsForCounts(externalId),
        getNotesCount(externalId),
      ]);
      if (!mountedRef.current) {
        return;
      }
      setConditionCounts({
        ...deriveConditionCounts(conditions),
        notes: notesCount,
      });
    } catch {
      if (!mountedRef.current) {
        return;
      }
      setConditionCounts(EMPTY_COUNTS);
    }
  }, [externalId]);

  const bumpConditionsRefresh = useCallback(() => {
    setConditionsRefreshKey((current) => current + 1);
  }, []);

  const refreshEmailStatus = useCallback(async () => {
    if (!losApplicationId) {
      return;
    }
    const requestId = ++emailStatusRequestIdRef.current;
    setEmailStatusState("loading");
    setEmailStatusError(null);
    try {
      const status = await getEmailStatus(losApplicationId);
      if (
        !mountedRef.current ||
        requestId !== emailStatusRequestIdRef.current
      ) {
        return;
      }
      setEmailStatus(status);
      setEmailStatusState("ready");
    } catch (error) {
      if (
        !mountedRef.current ||
        requestId !== emailStatusRequestIdRef.current
      ) {
        return;
      }
      setEmailStatusState("error");
      setEmailStatusError(
        error instanceof Error
          ? error.message
          : "Unable to load the pre-approval send status.",
      );
      throw error;
    }
  }, [losApplicationId]);

  const refreshPdf = useCallback(async () => {
    if (!losApplicationId) {
      setPdfState("error");
      setPdfError("Application identifiers are not available yet.");
      return "error";
    }
    const requestId = ++pdfRequestIdRef.current;
    if (!pdfUrlRef.current) {
      setPdfState("loading");
    }
    setPdfError(null);
    try {
      const blob = await getPreapprovalDocumentPdf(losApplicationId);
      if (
        blob.size === 0 ||
        (blob.type && !blob.type.includes("application/pdf"))
      ) {
        throw new Error("The generated document is not a valid PDF.");
      }
      if (!mountedRef.current || requestId !== pdfRequestIdRef.current) {
        return pdfUrlRef.current ? "ready" : "missing";
      }

      const nextUrl = URL.createObjectURL(blob);
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
      }
      pdfUrlRef.current = nextUrl;
      setPdfBlobUrl(nextUrl);
      setPdfState("ready");
      return "ready";
    } catch (error) {
      if (!mountedRef.current || requestId !== pdfRequestIdRef.current) {
        return pdfUrlRef.current ? "ready" : "missing";
      }
      const message = error instanceof Error ? error.message.toLowerCase() : "";
      const isMissingDocument =
        message.includes("404") ||
        message.includes("not found") ||
        message.includes("document not found");
      if (isMissingDocument) {
        setPdfState("missing");
        setPdfError(null);
        return "missing";
      }
      setPdfState("error");
      setPdfError(
        error instanceof Error
          ? error.message
          : "Unable to load the pre-approval document.",
      );
      throw error;
    }
  }, [losApplicationId]);

  useEffect(() => {
    if (bootstrapState !== "ready" || !losApplicationId) {
      return;
    }
    refreshEmailStatus().catch(() => undefined);
    refreshPdf().catch(() => undefined);
    refreshCounts().catch(() => undefined);
  }, [
    bootstrapState,
    losApplicationId,
    refreshCounts,
    refreshEmailStatus,
    refreshPdf,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pdfRequestIdRef.current += 1;
      emailStatusRequestIdRef.current += 1;
      bootstrapRequestIdRef.current += 1;
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
      }
    };
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!losApplicationId) {
      throw new Error("Application identifiers are not available yet.");
    }
    setActionState("generating");
    setGenerationError(null);
    try {
      const nextRecipients = await generateLetter(losApplicationId);
      setRecipients(nextRecipients);
      const nextPdfState = await refreshPdf();
      if (nextPdfState !== "ready") {
        throw new Error(
          "The document was generated but is not available for preview yet.",
        );
      }
    } catch (error) {
      setGenerationError(
        error instanceof Error
          ? error.message
          : "Unable to generate the pre-approval document.",
      );
      throw error;
    } finally {
      setActionState("idle");
    }
  }, [losApplicationId, refreshPdf]);

  const handleSync = useCallback(async () => {
    setActionState("syncing");
    setSyncError(null);
    try {
      const conditions = await syncToDigiFi(externalId);
      if (mountedRef.current) {
        const notesCount = await getNotesCount(externalId).catch(() => 0);
        setConditionCounts({
          ...deriveConditionCounts(conditions),
          notes: notesCount,
        });
        setConditionsRefreshKey((current) => current + 1);
      }
    } catch (error) {
      setSyncError(
        error instanceof Error
          ? error.message
          : "Unable to sync changes to DigiFi.",
      );
      throw error;
    } finally {
      setActionState("idle");
    }
  }, [externalId]);

  const openSendModal = useCallback(async () => {
    try {
      if (!recipients || pdfState !== "ready") {
        await handleGenerate();
      }
      setSendModalOpen(true);
    } catch {
      // handleGenerate exposes the actionable error in the workspace.
    }
  }, [handleGenerate, pdfState, recipients]);

  const togglePreviewMaximized = useCallback(() => {
    setIsPreviewMaximized((current) => !current);
  }, []);

  const value = useMemo(
    () => ({
      externalId,
      losApplicationId,
      application,
      bootstrapState,
      bootstrapError,
      actionState,
      generationError,
      syncError,
      emailStatus,
      emailStatusState,
      emailStatusError,
      pdfBlobUrl,
      pdfState,
      pdfError,
      recipients,
      isSendModalOpen,
      isPreviewMaximized,
      conditionCounts,
      conditionsRefreshKey,
      setSendModalOpen,
      togglePreviewMaximized,
      refreshEmailStatus,
      refreshCounts,
      bumpConditionsRefresh,
      handleGenerate,
      handleSync,
      openSendModal,
      refreshPdf,
    }),
    [
      externalId,
      losApplicationId,
      application,
      bootstrapState,
      bootstrapError,
      actionState,
      generationError,
      syncError,
      emailStatus,
      emailStatusState,
      emailStatusError,
      pdfBlobUrl,
      pdfState,
      pdfError,
      recipients,
      isSendModalOpen,
      isPreviewMaximized,
      conditionCounts,
      conditionsRefreshKey,
      togglePreviewMaximized,
      refreshEmailStatus,
      refreshCounts,
      bumpConditionsRefresh,
      handleGenerate,
      handleSync,
      openSendModal,
      refreshPdf,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return ctx;
}
